/**
 * The painted button chrome (`plate-key`, kit.css) on its own, with no imports.
 *
 * Split out of `ui.ts` because the arena's touch controls need it, and `ui.ts` pulls in i18n and
 * through it core's network layer, which reads `window` at import time — the arena scene's
 * node-environment tests (`landing-scene.test.ts`) cannot load that chain. `ui.ts` re-exports
 * everything here, so shell code keeps importing from `ui`.
 */

export type ButtonVariant = 'primary' | 'secondary' | 'selector' | 'ghost' | 'danger' | 'gold';
export type ButtonSize = 'md' | 'lg' | 'xl' | 'hero';

/**
 * The button is a painted key from the kit (`plate-key`, kit.css): a glossy enamel face — teal,
 * gold, red or steel — in a gunmetal rim with corner screws, worn as a 9-slice scaled by the face
 * height this function writes as `--pk-h`. Hover lifts it and sweeps a light across the face;
 * press sinks it.
 *
 * History, briefly: 2026-09-03 skewed parallelograms; 2026-09-07 rendered slabs; 2026-09-09 a CSS
 * plate drawn to the kit sheet; 2026-10-12 the painted kit ("no flat surface anywhere").
 * `skewed`/`lean`/`wedge`/`shape` stay in the API and are accepted, but the art has one shape.
 */
const BUTTON_BASE =
  'plate-key font-heading uppercase tracking-[0.08em] text-[13px] font-bold select-none touch-manipulation';

/** Face height per size, in CSS px; the art's caps scale with it so the cut angle is constant. */
const BUTTON_SIZES: Record<ButtonSize, { h: number; className: string }> = {
  md: { h: 44, className: '' },
  lg: { h: 56, className: 'text-[15px]' },
  xl: { h: 76, className: 'text-xl sm:text-2xl tracking-[0.06em]' },
  hero: { h: 116, className: 'text-[46px] md:text-[56px] tracking-[0.04em] leading-none' },
};

export interface PlateChromeOptions {
  variant?: Exclude<ButtonVariant, 'ghost'>;
  size?: ButtonSize;
  /** Accepted for API stability; the rendered art has one shape. */
  shape?: 'blade' | 'square' | 'wedge' | 'wedge-lg';
  className?: string;
}

/**
 * Dresses ANY element (a `<button>`, an `<a>`) as a kit button and puts `label` on its face.
 * `button()` uses it; the landing page uses it for its anchor CTAs. The element's own
 * `textContent` still resolves to the label — the chrome carries no words.
 */
export function plateChrome(el: HTMLElement, label: string | Node, opts: PlateChromeOptions = {}): HTMLElement {
  const variant = opts.variant ?? 'secondary';
  const size = BUTTON_SIZES[opts.size ?? 'md'];
  el.className = [BUTTON_BASE, size.className, opts.className ?? ''].filter(Boolean).join(' ');
  el.dataset.plate = variant;
  el.style.setProperty('--pk-h', `${size.h}px`);
  const text = document.createElement('span');
  text.className = 'plate-key-label';
  if (typeof label === 'string') text.textContent = label;
  else text.append(label);
  el.replaceChildren(text);
  return el;
}

/**
 * The node a plate's own content lives in — create it if the element has lost it.
 *
 * `plate-key` is two pseudo-element layers: `::before` is the rim and `::after` is the face,
 * and the ONLY thing that sits above the face is `& > .plate-key-label`, which carries
 * `z-index: 1`. So a caller that re-renders a plate's content with
 * `el.replaceChildren(icon, text)` deletes that child, and the face then paints over
 * everything it appended: the control renders as a correct, completely empty plate. That is
 * not a subtle bug to look at — the Daily reward launcher shipped it — but it is an invisible
 * one to write, which is why the correct move gets a name here instead of a comment there.
 *
 * Use this instead of touching a plate's children directly. `plateChrome` builds the same node.
 */
export function plateContent(el: HTMLElement): HTMLElement {
  // A direct-child scan rather than `:scope >` — the label must be a CHILD for the CSS to lift
  // it, and a descendant match would hand back a nested one from some caller's own markup.
  for (const child of Array.from(el.children)) {
    if (child instanceof HTMLElement && child.classList.contains('plate-key-label')) return child;
  }
  const label = document.createElement('span');
  label.className = 'plate-key-label';
  el.replaceChildren(label);
  return label;
}
